/**
 * Groups modifier options by their parent modifier
 * @param {Array} modifierOptions - Array of modifier options from item
 * @returns {Object} Grouped modifiers by modifier ID
 */
export const groupModifiersByParent = (modifierOptions) => {
    const modifierGroups = {};

    modifierOptions?.forEach((option) => {
        const modifierId = option.modifier?.id;
        if (!modifierId) return;

        if (!modifierGroups[modifierId]) {
            modifierGroups[modifierId] = {
                modifier: option.modifier,
                options: [],
            };
        }
        modifierGroups[modifierId].options.push(option);
    });

    return modifierGroups;
};

/**
 * Calculates total price including variation, modifiers, and quantity
 * @param {Object} params - Calculation parameters
 * @returns {Number} Total price
 */
export const calculateItemTotalPrice = ({
    item,
    selectedVariation,
    selectedModifiers,
    modifierGroups,
    quantity,
}) => {
    let total = 0;

    // Base price or variation price
    if (selectedVariation && item.variations) {
        const variation = item.variations.find((v) => v.id === selectedVariation);
        total = variation ? variation.price : item.price;
    } else {
        total = item.price;
    }

    // Add modifier prices
    Object.entries(selectedModifiers).forEach(([modifierId, optionIds]) => {
        const group = modifierGroups[modifierId];
        if (group) {
            optionIds.forEach((optionId) => {
                const option = group.options.find((o) => o.id === optionId);
                if (option) {
                    total += option.price;
                }
            });
        }
    });

    return total * quantity;
};

/**
 * Calculates the unit price for an item (without quantity multiplier)
 * @param {Object} item - The menu item
 * @param {String} selectedVariation - Selected variation ID
 * @param {Object} selectedModifiers - Selected modifiers by modifier ID
 * @param {Object} modifierGroups - Grouped modifiers
 * @returns {Number} Unit price
 */
export const calculateUnitPrice = ({
    item,
    selectedVariation,
    selectedModifiers,
    modifierGroups,
}) => {
    let total = 0;

    // Start with variation price or base price
    if (selectedVariation && item.variations) {
        const variation = item.variations.find((v) => v.id === selectedVariation);
        total = variation ? variation.price : item.price;
    } else {
        total = item.price;
    }

    // Add modifier prices
    Object.entries(selectedModifiers).forEach(([modifierId, optionIds]) => {
        const group = modifierGroups[modifierId];
        if (group) {
            optionIds.forEach((optionId) => {
                const option = group.options.find((o) => o.id === optionId);
                if (option) {
                    total += option.price;
                }
            });
        }
    });

    return total;
};

/**
 * Validates if all required modifiers are selected
 * @param {Object} modifierGroups - Grouped modifiers
 * @param {Object} selectedModifiers - Currently selected modifiers
 * @returns {Boolean} True if all requirements met
 */
export const validateRequiredModifiers = (
    modifierGroups,
    selectedModifiers,
) => {
    for (const [modifierId, group] of Object.entries(modifierGroups)) {
        if (group.modifier.isRequired) {
            const selections = selectedModifiers[modifierId] || [];
            if (selections.length < group.modifier.minSelections) {
                return false;
            }
        }
    }
    return true;
};

/**
 * Handles modifier selection toggle logic
 * @param {Object} params - Toggle parameters
 * @returns {Object} New selected modifiers state
 */
export const toggleModifier = ({
    currentSelections,
    modifierId,
    optionId,
    isRequired,
    maxSelections,
}) => {
    const selections = currentSelections[modifierId] || [];
    const isSelected = selections.includes(optionId);

    if (maxSelections === 1 || maxSelections === null) {
        // Radio behavior for single selection (null defaults to 1)
        return { ...currentSelections, [modifierId]: [optionId] };
    } else {
        // Checkbox behavior
        if (isSelected) {
            return {
                ...currentSelections,
                [modifierId]: selections.filter((id) => id !== optionId),
            };
        } else {
            // Check max selections
            if (selections.length >= maxSelections) {
                return currentSelections;
            }
            return {
                ...currentSelections,
                [modifierId]: [...selections, optionId],
            };
        }
    }
};
